import React, { useMemo, useRef } from "react";
import {
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  HABIT_ICON_OPTIONS,
  countCompletedSessions,
  getHabitCount,
  todayDateKey,
} from "../storage/customHabitsStorage.js";
import { useLanguage } from "../i18n/LanguageContext.js";

/**
 * @param {{ progress: number, size: number, stroke: number, color: string, trackColor?: string, children?: React.ReactNode }} props
 */
function HabitProgressRing({ progress, size, stroke, color, trackColor = "#E8ECF0", children }) {
  const clamped = Math.min(1, Math.max(0, progress));
  const quarter = (n) => (clamped >= n ? color : trackColor);

  return (
    <View style={{ width: size, height: size, alignItems: "center", justifyContent: "center" }}>
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderColor: trackColor,
        }}
      />
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderTopColor: quarter(0.001),
          borderRightColor: quarter(0.25),
          borderBottomColor: quarter(0.5),
          borderLeftColor: quarter(0.75),
          transform: [{ rotate: "-45deg" }],
        }}
      />
      {children}
    </View>
  );
}

/**
 * @param {{
 *   habits: import('../storage/customHabitsStorage.js').CustomHabit[],
 *   logs: import('../storage/customHabitsStorage.js').HabitLogs,
 *   onIncrement: (habitId: string) => void,
 *   onDecrement: (habitId: string) => void,
 *   onAddHabit: (payload: { name: string, icon: string, dailyGoal: number }) => void,
 *   onRemoveHabit: (habitId: string) => void,
 * }} props
 */
export function HabitProgressSection({
  habits,
  logs,
  onIncrement,
  onDecrement,
  onAddHabit,
  onRemoveHabit,
  darkMode = false,
}) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [draftName, setDraftName] = React.useState("");
  const [draftIcon, setDraftIcon] = React.useState("🎯");
  const dateKey = todayDateKey();
  const { t } = useLanguage();
  const carouselRef = useRef(null);

  const sessions = useMemo(() => countCompletedSessions(habits, logs, dateKey), [habits, logs, dateKey]);

  const openAdd = () => {
    setDraftName("");
    setDraftIcon("🎯");
    setAddOpen(true);
  };

  const submitAdd = () => {
    const name = draftName.trim();
    if (!name) {
      if (Platform.OS === "web") {
        if (typeof window !== "undefined") window.alert("Enter a name for your habit.");
      } else {
        Alert.alert("Habit name", "Enter a name for your habit.");
      }
      return;
    }
    onAddHabit({ name, icon: draftIcon, dailyGoal: 1 });
    setAddOpen(false);
  };

  const confirmRemove = (habit) => {
    const title = "Remove habit";
    const message = `Remove "${habit.name}" from your progress cards?`;
    if (Platform.OS === "web") {
      if (typeof window !== "undefined" && window.confirm(`${title}\n\n${message}`)) {
        onRemoveHabit(habit.id);
      }
      return;
    }
    Alert.alert(title, message, [
      { text: "Cancel", style: "cancel" },
      { text: "Remove", style: "destructive", onPress: () => onRemoveHabit(habit.id) },
    ]);
  };

  const s = darkMode ? darkStyles : styles;
  const cardWidth = 132;
  const cardGap = 12;
  const snapInterval = cardWidth + cardGap;

  return (
    <View style={s.wrap}>
      <View style={s.headerRow}>
        <Text style={s.headerTitle}>{t("progress_title")}</Text>
        <View style={s.headerActions}>
          <View style={s.sessionBadge}>
            <Text style={s.sessionTrend}>↗</Text>
            <Text style={s.sessionText}>
              {sessions} {t("progress_sessions")}
            </Text>
          </View>
          <Pressable style={s.addHeaderBtn} onPress={openAdd} hitSlop={8}>
            <Text style={s.addHeaderBtnText}>{t("progress_add")}</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView
        ref={carouselRef}
        horizontal
        nestedScrollEnabled
        directionalLockEnabled
        disableIntervalMomentum
        showsHorizontalScrollIndicator
        contentContainerStyle={s.carousel}
        style={s.carouselScroll}
        decelerationRate="fast"
        snapToInterval={Platform.OS === "ios" ? snapInterval : undefined}
        snapToAlignment="start"
        // Android: paging feels more reliable than pressable-wrapped cards.
        overScrollMode="never"
        scrollEventThrottle={16}
        keyboardShouldPersistTaps="handled"
      >
        {habits.map((habit) => {
          const count = getHabitCount(logs, habit.id, dateKey);
          const goal = habit.dailyGoal;
          const ringProgress = goal > 0 ? count / goal : 0;
          const complete = count >= goal;

          return (
            <View key={habit.id} style={s.card}>
              <View style={s.cardTopRow}>
                <Text style={s.cardLabel}>{habit.name.toUpperCase()}</Text>
                <Pressable
                  onPress={() => confirmRemove(habit)}
                  hitSlop={10}
                  style={s.removeHit}
                  accessibilityLabel={`Remove ${habit.name}`}
                >
                  <Text style={s.removeHitText}>×</Text>
                </Pressable>
              </View>
              <Text style={[s.cardTrend, complete ? s.cardTrendOn : null]}>{complete ? "↗" : "—"}</Text>

              <HabitProgressRing
                progress={ringProgress}
                size={88}
                stroke={7}
                color={habit.color}
                trackColor={darkMode ? "rgba(45,212,191,0.15)" : "#E8ECF0"}
              >
                <Text style={s.cardIcon}>{habit.icon}</Text>
              </HabitProgressRing>

              <Text style={s.cardRatio}>
                {count} / {goal}
              </Text>

              <View style={s.stepRow}>
                <Pressable style={s.stepBtn} onPress={() => onDecrement(habit.id)} hitSlop={8}>
                  <Text style={s.stepBtnText}>▼</Text>
                </Pressable>
                <Pressable style={s.stepBtn} onPress={() => onIncrement(habit.id)} hitSlop={8}>
                  <Text style={s.stepBtnText}>▲</Text>
                </Pressable>
              </View>
            </View>
          );
        })}

        <Pressable style={s.addCard} onPress={openAdd}>
          <Text style={s.addPlus}>+</Text>
          <Text style={s.addText}>{t("progress_add_habit")}</Text>
        </Pressable>
      </ScrollView>

      <Pressable style={s.addFullBtn} onPress={openAdd}>
        <Text style={s.addFullBtnText}>+ {t("progress_add_habit")}</Text>
      </Pressable>

      <Modal visible={addOpen} animationType="slide" transparent onRequestClose={() => setAddOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalSheet}>
            <Text style={styles.modalTitle}>{t("progress_add_habit")}</Text>
            <Text style={styles.modalHint}>{t("progress_add_hint")}</Text>

            <Text style={styles.fieldLabel}>{t("habit_name")}</Text>
            <TextInput
              value={draftName}
              onChangeText={(val) => setDraftName(val.slice(0, 15))}
              placeholder="e.g. Study or Organize"
              placeholderTextColor="#9CA3AF"
              style={styles.nameInput}
              maxLength={15}
            />
            <Text style={styles.charCount}>{draftName.length}/15</Text>

            <Text style={styles.fieldLabel}>{t("progress_icon")}</Text>
            <ScrollView
              horizontal
              nestedScrollEnabled
              directionalLockEnabled
              showsHorizontalScrollIndicator={false}
              style={styles.iconScroll}
              keyboardShouldPersistTaps="handled"
            >
              {HABIT_ICON_OPTIONS.map((icon) => (
                <Pressable
                  key={icon}
                  style={[styles.iconChip, draftIcon === icon ? styles.iconChipOn : null]}
                  onPress={() => setDraftIcon(icon)}
                >
                  <Text style={styles.iconChipText}>{icon}</Text>
                </Pressable>
              ))}
            </ScrollView>

            <View style={styles.modalActions}>
              <Pressable style={styles.cancelBtn} onPress={() => setAddOpen(false)}>
                <Text style={styles.cancelBtnText}>{t("progress_cancel")}</Text>
              </Pressable>
              <Pressable style={styles.saveBtn} onPress={submitAdd}>
                <Text style={styles.saveBtnText}>{t("progress_save_habit")}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginBottom: 16 },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingHorizontal: 2,
  },
  headerTitle: { fontSize: 22, fontWeight: "800", color: "#1e3a5f", letterSpacing: 0.5 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 10 },
  sessionBadge: { flexDirection: "row", alignItems: "center", gap: 4 },
  sessionTrend: { color: "#7c3aed", fontWeight: "800", fontSize: 14 },
  sessionText: { color: "#7c3aed", fontWeight: "700", fontSize: 14 },
  addHeaderBtn: {
    backgroundColor: "#0D9488",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
  },
  addHeaderBtnText: { color: "#fff", fontWeight: "800", fontSize: 13 },
  carouselScroll: { marginHorizontal: -4 },
  carousel: { paddingHorizontal: 4, gap: 12, paddingBottom: 4, paddingRight: 16 },
  card: {
    width: 132,
    backgroundColor: "#fff",
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#eef2f7",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardTopRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  cardLabel: {
    flex: 1,
    fontSize: 10,
    fontWeight: "700",
    color: "#9ca3af",
    letterSpacing: 0.6,
  },
  removeHit: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F3F4F6",
  },
  removeHitText: { color: "#6B7280", fontWeight: "900", fontSize: 16, lineHeight: 18 },
  cardTrend: {
    position: "absolute",
    top: 34,
    right: 10,
    fontSize: 12,
    color: "#d1d5db",
    fontWeight: "800",
  },
  cardTrendOn: { color: "#f97316" },
  cardIcon: { fontSize: 28 },
  cardRatio: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: "800",
    color: "#374151",
  },
  stepRow: {
    flexDirection: "row",
    gap: 16,
    marginTop: 6,
  },
  stepBtn: { padding: 4 },
  stepBtnText: { fontSize: 14, color: "#6b7280", fontWeight: "700" },
  addCard: {
    width: 132,
    minHeight: 180,
    backgroundColor: "#f8fafc",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
  },
  addPlus: { fontSize: 32, color: "#94a3b8", fontWeight: "300", marginBottom: 8 },
  addText: { fontSize: 12, color: "#64748b", fontWeight: "600", textAlign: "center", lineHeight: 18 },
  addFullBtn: {
    marginTop: 12,
    backgroundColor: "#0F766E",
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  addFullBtnText: { color: "#fff", fontWeight: "800", fontSize: 15 },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "flex-end",
  },
  modalSheet: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 20,
    paddingBottom: 32,
  },
  modalTitle: { fontSize: 20, fontWeight: "800", color: "#111" },
  modalHint: { fontSize: 13, color: "#6b7280", marginTop: 4, marginBottom: 16 },
  fieldLabel: { fontSize: 12, fontWeight: "700", color: "#374151", marginBottom: 6 },
  nameInput: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    backgroundColor: "#fafafa",
  },
  charCount: { fontSize: 11, color: "#9ca3af", textAlign: "right", marginTop: 4, marginBottom: 12 },
  iconScroll: { marginBottom: 16, maxHeight: 48 },
  iconChip: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: "#f3f4f6",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    borderWidth: 2,
    borderColor: "transparent",
  },
  iconChipOn: { borderColor: "#4f46e5", backgroundColor: "#eef2ff" },
  iconChipText: { fontSize: 22 },
  modalActions: { flexDirection: "row", justifyContent: "flex-end", gap: 10 },
  cancelBtn: { paddingVertical: 12, paddingHorizontal: 16 },
  cancelBtnText: { color: "#374151", fontWeight: "700" },
  saveBtn: {
    backgroundColor: "#4f46e5",
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 10,
  },
  saveBtnText: { color: "#fff", fontWeight: "800" },
});

const darkStyles = StyleSheet.create({
  ...StyleSheet.flatten(styles),
  headerTitle: { fontSize: 22, fontWeight: "800", color: "#5EEAD4", letterSpacing: 0.5 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 10 },
  sessionTrend: { color: "#22D3EE", fontWeight: "800", fontSize: 14 },
  sessionText: { color: "#2DD4BF", fontWeight: "700", fontSize: 14 },
  addHeaderBtn: {
    backgroundColor: "#14B8A6",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
  },
  addHeaderBtnText: { color: "#042F2E", fontWeight: "800", fontSize: 13 },
  addFullBtn: {
    marginTop: 12,
    backgroundColor: "#0D9488",
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.35)",
  },
  addFullBtnText: { color: "#ECFDF5", fontWeight: "800", fontSize: 15 },
  card: {
    width: 132,
    backgroundColor: "#132238",
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.22)",
  },
  cardTopRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  cardLabel: { flex: 1, fontSize: 10, fontWeight: "800", color: "#94A3B8", letterSpacing: 0.6 },
  removeHit: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(248,113,113,0.15)",
  },
  removeHitText: { color: "#FCA5A5", fontWeight: "900", fontSize: 16, lineHeight: 18 },
  cardTrend: { fontSize: 12, color: "#475569", marginBottom: 4 },
  cardTrendOn: { color: "#2DD4BF" },
  cardRatio: { fontSize: 13, fontWeight: "700", color: "#CCFBF1", marginTop: 6 },
  stepBtn: {
    width: 36,
    height: 28,
    borderRadius: 8,
    backgroundColor: "rgba(45, 212, 191, 0.12)",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.25)",
  },
  stepBtnText: { fontSize: 12, color: "#5EEAD4", fontWeight: "800" },
  addCard: {
    width: 132,
    backgroundColor: "rgba(45, 212, 191, 0.08)",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.35)",
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
  },
  addPlus: { fontSize: 28, color: "#2DD4BF", fontWeight: "300" },
  addText: { fontSize: 11, fontWeight: "700", color: "#5EEAD4", marginTop: 4, textAlign: "center" },
});
